import api from '@/api'
import dayjs from 'dayjs'

import pagesApi from '@/api/contentPages'

function hasProp(obj, prop) {
  return Object.prototype.hasOwnProperty.call(obj, prop)
}

function initialState() {
  return {
    pages: [],
    breadcrumb: [],
    menu: [],
    currentPage: {
      title: '',
      slug: 'this-is-a-slug',
      elements: [],
      review_date: '2019-03-02 12:00:00',
    },
    parentPage: [],
    currentChildren: [],
    tabFilter: null,
    filters: {
      searchQuery: '',
    },
    enablePagination: true,
    pagination: {
      total: 10,
      per_page: 10,
      current_page: 1,
      last_page: 1,
      from: 1,
      to: 10,
    },
  }
}

export default {
  namespaced: true,

  state: initialState,

  getters: {
    pageUrl(state) {
      const { breadcrumb } = state
      let pageUrl = '/'

      if (breadcrumb[0]) {
        breadcrumb.forEach((crumb) => {
          pageUrl += `${crumb.slug}/`
        })
      }

      return pageUrl
    },
  },

  mutations: {
    setPages(state, pages) {
      state.pages = pages
    },

    setCurrentPage(state, currentPage) {
      state.currentPage = Object.assign({}, currentPage)
    },

    setParentPage(state, parentPage) {
      state.parentPage = Object.assign({}, parentPage)
    },

    setBreadcrumb(state, breadcrumb) {
      state.breadcrumb = breadcrumb
    },

    setMenu(state, menu) {
      state.menu = menu
    },

    setTabFilter(state, tabFilter) {
      state.tabFilter = tabFilter
    },

    setSearchQuery(state, searchQuery) {
      state.filters.searchQuery = searchQuery
    },
    setPagination(state, value) {
      state.enablePagination = value
    },
    SET_FILTER_TOTAL(state, total) {
      state.pagination.total = total
      state.pagination.last_page = Math.ceil(total / state.pagination.per_page)
    },
    SET_CURRENT_PAGE(state, page) {
      state.pagination.current_page = page
    },

    SET_CHILDREN(state, children) {
      state.currentChildren = children
    },

    reset(state) {
      // acquire initial state
      const s = initialState()
      Object.keys(s).forEach((key) => {
        if (
          typeof s[key] === 'object' &&
          s[key] === Object(s[key]) &&
          s[key] !== null
        ) {
          state[key] = Object.assign({}, s[key])
        } else {
          state[key] = s[key]
        }
      })
    },
  },

  actions: {
    reset({ commit }) {
      commit('reset')
    },

    async list({ commit, state }, params) {
      const page = params && params.page ? params.page : 1
      const isDraft = params && params.draft ? params.draft : false
      const currentTab = state.tabFilter
      const searchQuery = state.filters.searchQuery || null
      commit('setPages', [])

      let size
      let from

      if (state.enablePagination) {
        size = state.pagination.per_page
        from = size * page - size

        if (!from) {
          from = 0
        }
      } else {
        size = 10000
        from = 0
      }

      const filter = {
        query: {
          bool: {
            must: [],
          },
        },
        size,
        from,
      }

      if (currentTab && currentTab !== 'draft') {
        // review and lapsed need to be date based
        switch (currentTab) {
          case 'review':
            filter.query.bool.must.push([
              {
                range: {
                  review_date: {
                    gte: dayjs().unix(),
                    lte: dayjs()
                      .add(14, 'day')
                      .unix(),
                  },
                },
              },
              {
                term: {
                  status: 'approved',
                },
              },
            ])
            break
          case 'lapsed':
            filter.query.bool.must.push([
              {
                range: {
                  review_date: {
                    lte: dayjs().unix(),
                  },
                },
              },
              {
                term: {
                  status: 'approved',
                },
              },
            ])
            break
          case 'pending':
            // if we are pending, we need to sort by modified_date
            filter.query.bool.must.push([
              {
                term: {
                  status: 'pending',
                },
              },
            ])
            filter.sort = [
              {
                modified_time: {
                  order: 'desc',
                },
              },
            ]
            break;
          // everything else is status
          default:
            filter.query.bool.must.push({
              term: {
                status: currentTab,
              },
            })
            break
        }
      }

      if (searchQuery) {
        filter.query.bool.must.push({
          bool: {
            should: [
              {
                match: {
                  title: {
                    query: searchQuery,
                    operator: 'and',
                    zero_terms_query: 'all',
                    fuzziness: 'AUTO',
                  },
                },
              },
              {
                match_phrase_prefix: {
                  title: {
                    query: searchQuery,
                    slop: 50,
                  },
                },
              },
            ],
          },
        })
      }

      let response

      /**
       * If we are looking for drafts, it needs to go to a different endpoint
       */
      const endpoint = isDraft ? '/content/drafts/flat' : '/content/pages/flat'

      const objectKey = isDraft ? 'draft' : 'page'
      try {
        response = await api().post(endpoint, { filter })
        commit('SET_FILTER_TOTAL', response.data.meta.filter_total)
        commit('SET_CURRENT_PAGE', page)
        let results
        if (isDraft) {
          results = response.data.data.draft
        } else {
          results = response.data.data.page
        }
        commit('setPages', results)
        return Promise.resolve(results)
      } catch (error) {
        if (error.response && hasProp(error.response.data.data, 'page')) {
          return Promise.resolve(error.response.data.data[objectKey])
        }
        return Promise.reject(error.response.data)
      }
    },

    async listDraftsById(context, idset) {
      const filter = {
        filter: {
          query: {
            bool: {
              must: {
                terms: {
                  page_id: idset
                }
              }
            }
          }
        }
      }
      const endpoint = '/content/drafts/flat'

      const objectKey = 'draft'
      try {
        const response = await api().post(endpoint, filter)
        return Promise.resolve(response.data.data.draft)
      } catch (error) {
        if (error.response && hasProp(error.response.data.data, 'page')) {
          return Promise.resolve(error.response.data.data[objectKey])
        }
        return Promise.reject(error.response.data)
      }
    },

    async query({ commit }, { query = {} }) {
      let resp

      try {
        resp = await api().put('/content/pages/flat', { filter: query })
        commit('SET_FILTER_TOTAL', resp.data.meta.filter_total)
        commit('SET_CURRENT_PAGE', 1)
        commit('setPages', resp.data.data.page)
        return Promise.resolve(resp.data.data.page)
      } catch (error) {
        // If there are no articles it returns a 404, so set the response here
        if (error.response.data.data.length < 1) {
          commit('setPages', [])
          return Promise.resolve([])
        }
        return Promise.reject(error)
      }
    },

    // this just gets a list of pages, we don't want to put them in state, just return some results
    async getParentPages() {
      const filter = {
        filter: {
          query: {
            bool: {
              must: {
                terms: {
                  status: ['approved', 'pending', 'new'],
                },
              },
            },
          },
          size: 10000,
        },
      }

      let response

      try {
        response = await api().post('/content/pages/flat', filter)
        return Promise.resolve(response.data.data.page)
      } catch (error) {
        if (error.response && hasProp(error.response.data.data, 'page')) {
          return Promise.resolve(error.response.data.data.page)
        }
        return Promise.reject(error.response.data)
      }
    },

    async getParentPagesForSelect({ state }, filterTerm) {
      state.loading = true
      let resp

      const query = {
        query: {
          bool: {
            must: [
              {
                terms: {
                  status: ['approved', 'pending', 'new'],
                },
              },
            ],
          },
        },
      }

      if (filterTerm) {
        query.query.bool.must.push({
          match_phrase_prefix: {
            title: {
              query: filterTerm,
              slop: 10,
            },
          },
        })
      }

      query.size = 10
      query.from = 0

      try {
        resp = await api().put('/content/pages/flat', { filter: query })
        // once we have data, format it into an id/label object
        const toReturn = resp.data.data.page.map((item) => ({
          id: item.id,
          title: item.title,
        }))
        return Promise.resolve(toReturn)
      } catch (error) {
        if (
          error.response.status === 404 &&
          error.response.data.data.length === 0
        ) {
          state.loading = false
          return Promise.resolve([])
        }
        return Promise.reject(error.response.data)
      }
    },

    create({ commit, dispatch, state, rootGetters }, pageData) {
      pageData = pageData || Object.assign({}, state.currentPage)
      pageData.title = pageData.title || 'Untitled Page'
      pageData.elements = rootGetters['pageElements/forServer'] || null

      return new Promise((resolve, reject) => {
        pagesApi
          .create(pageData)
          .then((page) => {
            resolve(page)
            dispatch(
              'notifications/create',
              {
                body: `The page <b>${page.title}</b> was created`,
                type: 'success',
              },
              { root: true }
            )
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    read({ commit, dispatch }, { pageSlugOrId, isAdmin }) {
      commit('setCurrentPage', {})
      commit('setParentPage', [])

      if (!isAdmin) {
        commit('setMenu', [])
      }

      // Checks if is a number when is digits string
      const isPageId = /^\d+$/.test(pageSlugOrId)
      const readBy = isPageId ? 'byId' : 'bySlug'

      return new Promise((resolve, reject) => {
        pagesApi.read[readBy](pageSlugOrId)
          .then((page) => {
            commit('setCurrentPage', page)
            if (!isAdmin) {
              dispatch('listBreadcrumb', page.id)
              dispatch('listMenu', page.id)
              if (page.parent_id) {
                // dispatch('readParent', page.parent_id);
              }
            }
            resolve(page)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    readParent({ commit }, parentPageId) {
      // commit('setParentPage', {});
      // return Promise.resolve({});

      commit('setParentPage', {})

      return new Promise((resolve, reject) => {
        pagesApi.read
          .byId(parentPageId)
          .then((page) => {
            commit('setParentPage', page)
            resolve(page)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    update({ commit, dispatch, rootGetters }, { pageId, pageData }) {
      pageData = pageData || Object.assign({}, state.currentPage)
      pageData.title = pageData.title || 'Untitled Page'
      pageData.elements = rootGetters['pageElements/forServer'] || null

      return new Promise((resolve, reject) => {
        pagesApi
          .update(pageId, pageData)
          .then((page) => {
            commit('setCurrentPage', page)
            dispatch(
              'notifications/create',
              {
                body: `The page <b>${page.title}</b> was updated`,
                type: 'success',
              },
              { root: true }
            )
            resolve(page)
          })
          .catch((error) => {
            if (error && error.data.error.status_code == 400) {
              dispatch(
                'notifications/create',
                {
                  title: 'Error!',
                  body: error.data.error.message,
                  type: 'danger',
                },
                { root: true }
              )
            }
            reject(error)
          })
      })
    },

    async resubmit({ dispatch }, { pageId, pageData }) {
      let response

      try {
        response = await api().post(`/content/page/${pageId}/resubmit`, {
          page: pageData,
        })
        dispatch(
          'notifications/create',
          {
            body: `The page <b>${response.data.data.page[0].title}</b> was re-submitted`,
            type: 'success',
          },
          { root: true }
        )
        return Promise.resolve(response.data.data.page)
      } catch (err) {
        dispatch(
          'notifications/create',
          {
            body: 'There was a problem saving the page.',
            type: 'danger',
          },
          { root: true }
        )
        return Promise.reject(new Error(err))
      }
    },

    delete({ dispatch }, pageId) {
      return new Promise((resolve, reject) => {
        pagesApi
          .delete(pageId)
          .then((page) => {
            resolve(page)
            dispatch('list')
            dispatch(
              'notifications/create',
              {
                body: `The page <b>${page.title}</b> was deleted`,
                type: 'success',
              },
              { root: true }
            )
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    async saveDraft(context, args) {
      const { pageId, ...payload } = args
      payload.elements = context.rootGetters['pageElements/forServer'] || null
      let response

      try {
        response = await api().post(`/content/page/${pageId}/draft`, {
          draft: payload,
        })
        return Promise.resolve(response)
      } catch (err) {
        return Promise.reject(new Error(err))
      }
    },

    async getDraft(context, args) {
      const { pageId } = args
      let response

      try {
        response = await api().get(`/content/page/${pageId}/draft`)
        // TODO: Should put details in store?
        return Promise.resolve(response.data.data.draft[0])
      } catch (err) {
        return Promise.reject(new Error(err))
      }
    },

    async deleteDraft(context, pageId) {
      let response

      try {
        response = await api().delete(`/content/page/${pageId}/draft`)
        return Promise.resolve(response)
      } catch (err) {
        return Promise.reject(new Error(err))
      }
    },

    listBreadcrumb({ commit }, pageId) {
      commit('setBreadcrumb', [])

      return new Promise((resolve, reject) => {
        api()
          .get(`/content/page/${pageId}/breadcrumb`)
          .then((response) => {
            const breadcrumb = response.data.data.breadcrumb[0]
            commit('setBreadcrumb', breadcrumb)
            resolve(breadcrumb)
          })
          .catch((error) => {
            reject(error.response)
          })
      })
    },

    listMenu({ commit }, pageId) {
      return new Promise((resolve, reject) => {
        api()
          .get(`/content/page/${pageId}/menu`)
          .then((response) => {
            const menu = response.data.data.menu
            commit('setMenu', menu)
            resolve(menu)
          })
          .catch((error) => {
            if (error.response && hasProp(error.response.data.data, 'menu')) {
              const menu = error.response.data.data.menu
              commit('setMenu', menu)
              resolve(menu)
            } else {
              reject(error.response.data)
            }
          })
      })
    },

    // --------------------

    async approve({ dispatch }, pageId) {
      let response

      try {
        response = await api().put(`/content/page/${pageId}/approve`)
        const page = response.data.data.page[0]
        dispatch('list')
        dispatch(
          'notifications/create',
          {
            body: `The page <b>${page.title}</b> was approved`,
            type: 'success',
          },
          { root: true }
        )
        return Promise.resolve(page)
      } catch (err) {
        return Promise.reject(err)
      }
    },

    reject({ commit, dispatch }, params) {
      return new Promise((resolve, reject) => {
        pagesApi
          .reject({ pageId: params.pageId, reason: params.reason })
          .then((page) => {
            resolve(page)
          })
          .catch((error) => {
            reject(error)
          })
      })
    },

    async getMenuChildren(context, pageId) {
      const { commit } = context
      const url = `/content/page/${pageId}/menuchildren/flat`
      let response

      try {
        response = await api().get(url)
        commit('SET_CHILDREN', response.data.data.menuchildren[0])
        return Promise.resolve(response.data.data.menuchildren[0])
      } catch (err) {
        return Promise.resolve(err)
      }
    },
  },
}
